import React, { useEffect, useRef } from "react";
import { createChart } from "lightweight-charts";
import { useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import formatMoney from "lib/formatMoney";

const PayoffChart = ({ data }) => {
  const chartContainerRef = useRef();
  const theme = useTheme();

  useEffect(() => {
    if (!data || data.length === 0) return;

    const chartOptions = {
      layout: {
        textColor: theme.palette.text.primary,
        border: { color: "transparent !important" },
      },
      grid: {
        vertLines: { color: theme.palette.grey[200] },
        horzLines: { color: theme.palette.grey[200] },
      },
      rightPriceScale: {
        borderVisible: false,
        scaleMargins: {
          top: 0.1,
          bottom: 0.1,
        },
        priceFormat: {
          type: "custom",
          formatter: (price) => `$${formatMoney(price, 0)}`,
        },
        border: { color: "transparent !important" },
      },
      leftPriceScale: {
        visible: true,
        borderVisible: false,
        scaleMargins: {
          top: 0.1,
          bottom: 0.1,
        },
        priceFormat: {
          type: "custom",
          formatter: (price) => `$${formatMoney(price, 0)}`,
        },
      },
      timeScale: {
        borderVisible: false,
        timeVisible: true,
        rightOffset: 0,
        fixLeftEdge: true,
        fixRightEdge: true,
        rightBarStaysOnScroll: true,
      },
      crosshair: {
        vertLine: {
          labelBackgroundColor: theme.palette.grey[800],
          color: theme.palette.grey[400],
        },
        horzLine: {
          labelBackgroundColor: theme.palette.grey[800],
          color: theme.palette.grey[400],
        },
      },
    };

    const chart = createChart(chartContainerRef.current, chartOptions);

    // Balance line - on left scale
    const balanceSeries = chart.addAreaSeries({
      lineColor: theme.palette.primary.main,
      topColor: theme.palette.primary.light,
      bottomColor: theme.palette.background.paper,
      lineWidth: 2,
      title: "Balance",
      priceFormat: {
        type: "custom",
        formatter: (price) => `$${formatMoney(price, 0)}`,
      },
      lastValueVisible: false,
      priceLineVisible: false,
      priceScaleId: "left",
    });

    // Total payment line - on right scale
    const totalPaymentSeries = chart.addLineSeries({
      color: theme.palette.grey[800],
      lineWidth: 3,
      title: "Total Payment",
      priceFormat: {
        type: "custom",
        formatter: (price) => `$${formatMoney(price, 0)}`,
      },
      lastValueVisible: false,
      priceLineVisible: false,
      priceScaleId: "right",
    });

    // Principal payments line - on right scale
    const principalSeries = chart.addLineSeries({
      color: theme.palette.success.main,
      lineWidth: 2,
      title: "Principal",
      lastValueVisible: false,
      priceLineVisible: false,
      priceScaleId: "right",
    });

    // Interest payments line - on right scale
    const interestSeries = chart.addLineSeries({
      color: theme.palette.warning.main,
      lineWidth: 2,
      title: "Interest",
      lastValueVisible: false,
      priceLineVisible: false,
      priceScaleId: "right",
    });

    // Add legend
    chart.applyOptions({
      legend: {
        visible: true,
        position: "top",
        markers: [
          { color: theme.palette.primary.main, type: "line" },
          { color: theme.palette.grey[800], type: "line" },
          { color: theme.palette.success.main, type: "line" },
          { color: theme.palette.warning.main, type: "line" },
        ],
        text: ["Balance", "Total Payment", "Principal", "Interest"],
      },
    });

    // Format data for the chart
    const chartData = data.map((row) => ({
      time: new Date(row.date).getTime() / 1000,
      value: row.balance,
      principal: row.principal,
      interest: row.interest,
      totalPayment: row.principal + row.interest,
      date: row.date,
    }));

    balanceSeries.setData(
      chartData.map((d) => ({ time: d.time, value: d.value }))
    );
    totalPaymentSeries.setData(
      chartData.map((d) => ({ time: d.time, value: d.totalPayment }))
    );
    principalSeries.setData(
      chartData.map((d) => ({ time: d.time, value: d.principal }))
    );
    interestSeries.setData(
      chartData.map((d) => ({ time: d.time, value: d.interest }))
    );

    // Update tooltip to include total payment
    chart.subscribeCrosshairMove((param) => {
      const toolTip = document.getElementById("payoff-chart-tooltip");

      // Hide tooltip if mouse is outside chart area
      if (
        !param.time ||
        param.point.x < 0 ||
        param.point.x > chartContainerRef.current.clientWidth ||
        param.point.y < 0 ||
        param.point.y > chartContainerRef.current.clientHeight
      ) {
        if (toolTip) {
          toolTip.style.display = "none";
        }
        return;
      }

      const data = chartData.find((d) => d.time === param.time);
      if (data && toolTip) {
        const tooltipHtml = `
          <div style="font-size: 12px; padding: 8px;">
            <div style="color: ${theme.palette.text.primary};">${
          data.date
        }</div>
            <div style="color: ${
              theme.palette.primary.main
            };">Balance: $${formatMoney(data.value)}</div>
            <div style="color: ${
              theme.palette.grey[800]
            };">Total Payment: $${formatMoney(data.totalPayment)}</div>
            <div style="color: ${
              theme.palette.success.main
            };">Principal: $${formatMoney(data.principal)}</div>
            <div style="color: ${
              theme.palette.warning.main
            };">Interest: $${formatMoney(data.interest)}</div>
          </div>
        `;

        toolTip.innerHTML = tooltipHtml;
        toolTip.style.display = "block";

        // Use relative positioning within the chart container
        toolTip.style.left = `${param.point.x}px`;
        toolTip.style.top = `${param.point.y - 10}px`; // Small offset to position above cursor
      }
    });

    // After setting the data, ensure the last point is visible
    chart.timeScale().fitContent();

    // Force the last date to be visible
    const lastDataPoint = chartData[chartData.length - 1];
    if (lastDataPoint) {
      chart.timeScale().scrollToPosition(0, true);
    }

    // Fit content and handle resize
    chart.timeScale().fitContent();

    const handleResize = () => {
      chart.applyOptions({
        width: chartContainerRef.current.clientWidth,
      });
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.remove();
    };
  }, [data, theme]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        "& table": {
          border: "0px !important",
        },
        "& td": {
          border: "0px !important",
        },
        "& td div": {
          border: "0px !important",
        },
      }}
    >
      <div
        ref={chartContainerRef}
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          outline: "none",
          position: "relative",
        }}
      />
      <div
        id="payoff-chart-tooltip"
        style={{
          position: "absolute",
          display: "none",
          padding: "8px",
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "4px",
          pointerEvents: "none",
          zIndex: 9999,
          transform: "translate(-50%, -100%)",
          boxShadow: theme.shadows[2],
        }}
      />
    </Box>
  );
};

export default PayoffChart;
